import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import styles from "./Section.module.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { isNonEmptyArray } from "../../../utils";
import Block from "../block/Block";
import { icon, IconComponent } from "../../icon-component";
import { generateLayout, isValidAndNonOverlapping, regenerateCorrectLayout } from "./utils";

const ResponsiveGridLayout = WidthProvider(Responsive);
const DEFAULT_COLS = 4;

const getColsForBasis = (basis) => ({
	lg: basis,
	md: Math.round(basis * 0.75),
	sm: Math.round(basis * 0.5),
	xs: Math.round(basis * 0.25),
	xxs: 1,
});

const repackLayout = (lgLayout, cols) => {
	if (!isNonEmptyArray(lgLayout)) {
		return [];
	}
	const sorted = [...lgLayout].sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
	const result = [];
	let cursorX = 0;
	let cursorY = 0;
	let rowHeight = 0;
	for (const item of sorted) {
		const w = Math.min(item.w, cols);
		const wouldOverflow = cursorX + w > cols;
		if (wouldOverflow) {
			if (result.length > 0) {
				const lastItem = result[result.length - 1];
				if (lastItem.y === cursorY && lastItem.x + lastItem.w < cols) {
					lastItem.w = cols - lastItem.x;
				}
			}
			cursorY += rowHeight;
			cursorX = 0;
			rowHeight = 0;
		}
		result.push({ ...item, x: cursorX, y: cursorY, w });
		cursorX += w;
		rowHeight = Math.max(rowHeight, item.h);
	}
	if (result.length > 0) {
		const lastItem = result[result.length - 1];
		if (lastItem.x + lastItem.w < cols) {
			lastItem.w = cols - lastItem.x;
		}
	}
	return result;
};

export default function Section({
	breakpoint,
	colBasis = DEFAULT_COLS,
	customPageId,
	editMode = false,
	hasEditorRights,
	projectId,
	section,
	onCollapseSection,
	onGlobalUpdate,
}) {
	const [layouts, setLayouts] = useState({ lg: [], md: [], sm: [], xs: [], xxs: [] });

	const hasOpenableSubsection = useMemo(
		() => isNonEmptyArray(section?.subsection) && !section?.open && section?.open !== undefined,
		[section]
	);

	const cols = useMemo(() => getColsForBasis(colBasis), [colBasis]);

	const buildLayouts = useCallback(
		(lgLayout) => {
			const singleCol = repackLayout(lgLayout, cols.xs);
			return {
				lg: lgLayout,
				md: repackLayout(lgLayout, cols.md),
				sm: repackLayout(lgLayout, cols.sm),
				xs: singleCol,
				xxs: singleCol,
			};
		},
		[cols]
	);

	useEffect(() => {
		const { layout } = section;
		const filteredLayout = (layout || []).filter((block) => !block?.hidden);
		const lgLayout =
			(isNonEmptyArray(filteredLayout) &&
				((isValidAndNonOverlapping(filteredLayout, colBasis) && generateLayout(filteredLayout, editMode)) ||
					regenerateCorrectLayout(filteredLayout, editMode, colBasis))) ||
			[];
		setLayouts(buildLayouts(lgLayout));
	}, [editMode, section, colBasis, buildLayouts]);

	const handleLayoutChange = (_, allLayouts) => {
		if (!allLayouts.lg) {
			return;
		}
		setLayouts(buildLayouts(allLayouts.lg));
	};

	return (
		<div className={styles.container} data-has-openable-subsection={hasOpenableSubsection}>
			<div
				className={styles.header}
				role="presentation"
				onClick={() => {
					const identifier = section?.uuid || section?.id;
					onCollapseSection(identifier);
				}}
			>
				<IconComponent
					color="var(--grey-600)"
					icon={icon[(section?.open && "faChevronUp") || "faChevronDown"]}
				/>
				<span>{section.name}</span>
			</div>
			<div className={styles.sectionContainer} data-open={section?.open === undefined || !!section?.open}>
				<>
					<ResponsiveGridLayout
						breakpoints={{ lg: 1280, md: 1024, sm: 768, xs: 480, xxs: 0 }}
						cols={cols}
						compactType="vertical"
						isDraggable={editMode}
						isResizable={editMode}
						layouts={layouts}
						margin={[0, 0]}
						measureBeforeMount={false}
						rowHeight={126}
						onLayoutChange={handleLayoutChange}
					>
						{isNonEmptyArray(section?.layout) &&
							section.layout
								.filter((block) => !block?.hidden)
								.map((block) => (
									<div key={block.id} className={styles.block}>
										<Block
											block={block}
											customPageId={customPageId}
											editMode={editMode}
											hasEditorRights={hasEditorRights}
											projectId={projectId}
											sectionId={section.id}
											onGlobalUpdate={onGlobalUpdate}
										/>
									</div>
								))}
					</ResponsiveGridLayout>
					{isNonEmptyArray(section?.subsection) &&
						section.subsection.map((sub) => (
							<div key={sub.id} className={styles.subsectionContainer}>
								<Section
									isSubSection
									breakpoint={breakpoint}
									colBasis={colBasis}
									customPageId={customPageId}
									editMode={editMode}
									hasEditorRights={hasEditorRights}
									projectId={projectId}
									section={sub}
									onCollapseSection={onCollapseSection}
									onGlobalUpdate={onGlobalUpdate}
								/>
							</div>
						))}
				</>
			</div>
		</div>
	);
}
